import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import voterService from '../api/voterService';
import bridgeService from '../api/bridgeService';
import { authFetch } from '../api/authFetch';
import pricingService from '../api/pricingService';
import { useCounties } from '../contexts/CountiesContext';
import { useAuth } from '../contexts/AuthContext';
import QueryWalkthroughModal from '../components/modals/QueryWalkthroughModal';
import CheckoutPopup from '../components/CheckoutPopup';
import { saveMailingListSearch } from '../utils/mailingListSearch';
import './VotersPage.css';

// Must match the largest tier the server sells (DoorKnockServer
// routes/mailvotes/pricing.js). Bigger lists cannot be delivered as one CSV.
const MAX_PRICED_COUNT = 50000;

function VotersPage() {
  // Use counties context instead of managing state locally
  const { getCountiesForState, availableStates, allCounties } = useCounties();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [activeFilters, setActiveFilters] = useState({});
  const [isWalkthroughModalOpen, setIsWalkthroughModalOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [recordCount, setRecordCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isCountLoading, setIsCountLoading] = useState(false);
  const [voterData, setVoterData] = useState([]);
  const [counties, setCounties] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [elections, setElections] = useState([]);
  const [isElectionsLoading, setIsElectionsLoading] = useState(false);
  const [sortField, setSortField] = useState('LastName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [latestPurchase, setLatestPurchase] = useState(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState(null);
  const [sendToDoorNocIntent, setSendToDoorNocIntent] = useState(false);
  const downloadLinkRef = useRef(null);
  const abortControllerRef = useRef(null);

  const [priceQuote, setPriceQuote] = useState(null);
  const [quoteError, setQuoteError] = useState('');

  const formatPrice = (priceInCents) => {
    return (priceInCents / 100).toFixed(2);
  };

  useEffect(() => {
    let cancelled = false;
    async function fetchQuote() {
      if (!totalCount || totalCount < 1) {
        setPriceQuote(null);
        setQuoteError('');
        return;
      }
      if (totalCount > MAX_PRICED_COUNT) {
        setPriceQuote(null);
        setQuoteError(`List exceeds ${MAX_PRICED_COUNT.toLocaleString()} voters — narrow your search or contact sales.`);
        return;
      }
      try {
        const data = await pricingService.fetchQuote(totalCount);
        if (cancelled) return;
        if (data.success) {
          setPriceQuote(data);
          setQuoteError('');
        } else {
          setPriceQuote(null);
          setQuoteError(data.error || 'Unable to calculate price');
        }
      } catch (err) {
        if (!cancelled) {
          setPriceQuote(null);
          setQuoteError(err.message || 'Unable to reach pricing service');
        }
      }
    }
    fetchQuote();
    return () => { cancelled = true; };
  }, [totalCount]);

  // Fetch elections on component mount (counties are handled by context)
  useEffect(() => {
    fetchElections();

    // Auto-open walkthrough modal on page load
    setIsWalkthroughModalOpen(true);

    // Create a hidden download link element that will persist
    const link = document.createElement("a");
    link.style.display = "none";
    document.body.appendChild(link);
    downloadLinkRef.current = link;

    // Cleanup function - remove the link and cancel any pending requests when component unmounts
    return () => {
      if (downloadLinkRef.current && document.body.contains(downloadLinkRef.current)) {
        document.body.removeChild(downloadLinkRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Reset pagination when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [voterData]);

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Update counties when state changes
  useEffect(() => {
    if (selectedState) {
      const stateCounties = getCountiesForState(selectedState);
      setCounties(stateCounties);
    } else {
      // Show all counties with state labels when no state is selected
      const allCountiesFormatted = allCounties
        .filter(item => item.county && item.state)
        .map(item => `${item.county} (${item.state})`)
        .sort();
      setCounties(allCountiesFormatted);
    }
  }, [selectedState, allCounties, getCountiesForState]);

  // Handle state selection change
  const handleStateChange = (state) => {
    setSelectedState(state);
    // Counties will be updated automatically by useEffect
    
    // Clear county selection when state changes
    const countyInput = document.querySelector('#countyInput');
    if (countyInput) {
      countyInput.value = '';
    }
  };

  // Fetch elections from API
  const fetchElections = async () => {
    setIsElectionsLoading(true);
    try {
      const data = await voterService.getElections();

      if (data.success) {
        setElections(data.data || []);
      } else {
        console.error('Error fetching elections:', data.error);
        setElections([]);
      }
    } catch (error) {
      console.error('Error fetching elections:', error);
      setElections([]);
    } finally {
      setIsElectionsLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Open query walkthrough modal
  const openWalkthroughModal = () => {
    setIsWalkthroughModalOpen(true);
  };

  // Close query walkthrough modal
  const closeWalkthroughModal = () => {
    setIsWalkthroughModalOpen(false);
  };

  // Handle filters from walkthrough modal
  const handleApplyWalkthroughFilters = async (filters) => {
    // Update active filters
    setActiveFilters(filters);

    // Set the filter values in the UI inputs for visibility
    if (filters.FirstName) document.querySelector('#firstNameInput').value = filters.FirstName;
    if (filters.LastName) document.querySelector('#lastNameInput').value = filters.LastName;
    if (filters.City) document.querySelector('#cityInput').value = filters.City;
    if (filters.State) {
      document.querySelector('#stateInput').value = filters.State;
      handleStateChange(filters.State);
    }
    if (filters.County_Desc) document.querySelector('#countyInput').value = filters.County_Desc;
    if (filters.PolitalAff) document.querySelector('#politicalAffiliationInput').value = filters.PolitalAff;
    if (filters.MuniSub) document.querySelector('#muniSubInput').value = filters.MuniSub;
    if (filters.Precinct) document.querySelector('#precinctInput').value = filters.Precinct;
    if (filters.StreetName) document.querySelector('#streetNameInput').value = filters.StreetName;
    if (filters.StreetNum) document.querySelector('#streetNumInput').value = filters.StreetNum;
    if (filters.VoterID) document.querySelector('#voterIdInput').value = filters.VoterID;
    if (filters.Status) document.querySelector('#statusInput').value = filters.Status;

    // Cancel any existing search request before starting new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      // Stop current spinner immediately
      setIsLoading(false);
      // Small delay to ensure spinner visually stops before restarting
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    // Show loading spinner for new search
    setIsLoading(true);

    try {
      // Call the search API with the filters from the modal
      await searchVoters(filters);
    } catch (error) {
      // Don't show error for aborted requests
      if (error.name === 'AbortError' || 
          error.message?.includes('aborted') || 
          error.message?.includes('canceled') ||
          error.code === 'ERR_CANCELED') {
        console.log('Search request was cancelled');
        return;
      }
      console.error('Error during search:', error);
      alert('An error occurred while fetching records. Please try again later or check your query.');
    } finally {
      // Hide loading spinner
      setIsLoading(false);
    }
  };

  // Handle search
  const handleSearch = async () => {
    const filters = {
      FirstName: document.querySelector('#firstNameInput')?.value.trim() || '',
      LastName: document.querySelector('#lastNameInput')?.value.trim() || '',
      City: document.querySelector('#cityInput')?.value.trim() || '',
      State: document.querySelector('#stateInput')?.value.trim() || '',
      County_Desc: (() => {
        const countyValue = document.querySelector('#countyInput')?.value.trim() || '';
        // Extract just the county name if it's formatted as "COUNTY (STATE)"
        return countyValue.includes(' (') ? countyValue.split(' (')[0] : countyValue;
      })(),
      PolitalAff: document.querySelector('#politicalAffiliationInput')?.value.trim() || '',
      CongressionalDist: document.querySelector('#congressionalDistInput')?.value.trim() || '',
      StateSenate: document.querySelector('#stateSenateDistInput')?.value.trim() || '',
      StateHouse: document.querySelector('#stateHouseDistInput')?.value.trim() || '',
      SchoolDist: document.querySelector('#schoolDistInput')?.value.trim() || '',
      MuniSub: document.querySelector('#muniSubInput')?.value.trim() || '',
      Precinct: document.querySelector('#precinctInput')?.value.trim() || '',
      StreetName: document.querySelector('#streetNameInput')?.value.trim() || '',
      StreetNum: document.querySelector('#streetNumInput')?.value.trim() || '',
      VoterID: document.querySelector('#voterIdInput')?.value.trim() || '',
      Status: document.querySelector('#statusInput')?.value.trim() || ''
    };

    // Store active filters for later use
    const newActiveFilters = Object.keys(filters).reduce((acc, key) => {
      if (filters[key]) acc[key] = filters[key];
      return acc;
    }, {});
    setActiveFilters(newActiveFilters);

    // Cancel any existing search request before starting new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      // Stop current spinner immediately
      setIsLoading(false);
      // Small delay to ensure spinner visually stops before restarting
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    // Show loading spinner for new search
    setIsLoading(true);

    try {
      // Call the search API
      await searchVoters(newActiveFilters);
    } catch (error) {
      // Don't show error for aborted requests
      if (error.name === 'AbortError' || 
          error.message?.includes('aborted') || 
          error.message?.includes('canceled') ||
          error.code === 'ERR_CANCELED') {
        console.log('Search request was cancelled');
        return;
      }
      console.error('Error during search:', error);
      alert('An error occurred while fetching records. Please try again later or check your query.');
    } finally {
      // Hide loading spinner
      setIsLoading(false);
    }
  };

  // Search voters from API
  const searchVoters = async (filters) => {
    try {
      console.log('Searching voters with filters:', filters);

      // Cancel any existing search request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      // Clear React state
      setVoterData([]);
      setRecordCount(0);
      setTotalCount(0);
      setCurrentPage(1);
      setIsCountLoading(false);
      // A new search produces a new list — previous purchase no longer applies
      setLatestPurchase(null);

      const data = await voterService.searchVoters(filters, abortControllerRef.current.signal);

      if (!data.success) {
        throw new Error(data.error || 'An error occurred while searching for voters');
      }

      const voters = data.data || [];
      console.log(`Records returned: ${voters.length}, Total matching records: ${data.totalCount || 'unknown'}`);

      // Set both the visible record count and the total count
      setRecordCount(voters.length);
      setTotalCount(data.totalCount || voters.length);

      // The Postcards page mails to this search (see utils/mailingListSearch).
      saveMailingListSearch(filters, data.totalCount || voters.length);

      // If this is a state-only search, get the accurate count separately
      const isStateOnlySearch = filters.State && 
        !filters.FirstName && !filters.LastName && !filters.VoterID &&
        Object.keys(filters).filter(k => {
          const value = filters[k];
          if (typeof value === 'number') return value !== null && value !== undefined;
          if (typeof value === 'string') return value && value.trim() !== '';
          return value;
        }).length === 1;
      
      if (isStateOnlySearch) {
        console.log('Getting accurate count for state search...');
        setIsCountLoading(true);
        // Get count in background without blocking the UI
        voterService.getStateCount({ State: filters.State }, abortControllerRef.current.signal)
          .then(countData => {
            if (countData.success) {
              console.log(`Updated count for ${filters.State}: ${countData.totalCount}`);
              setTotalCount(countData.totalCount);
            }
          })
          .catch(countError => {
            if (countError.name !== 'AbortError') {
              console.warn('Could not get accurate count:', countError);
            }
            // Keep the original count if separate count fails
          })
          .finally(() => {
            setIsCountLoading(false);
          });
      }

      // Set voter data directly
      setVoterData(voters);
    } catch (error) {
      // Don't show error for aborted requests
      if (error.name === 'AbortError' || 
          error.message?.includes('aborted') || 
          error.message?.includes('canceled') ||
          error.code === 'ERR_CANCELED') {
        console.log('Search request was cancelled');
        return;
      }

      console.error('Error searching voters:', error);
      
      // Show error state silently
      setRecordCount(0);
      setTotalCount(0);
      setVoterData([]);
    }
  };


  const [isSendingToDoorNoc, setIsSendingToDoorNoc] = useState(false);
  const [transferResult, setTransferResult] = useState(null);

  // Send voter IDs to DoorNoc via bridge — requires a completed purchase.
  // The client only ships purchaseId; the server re-runs the stored search
  // to collect the FULL voter list (the browser only has the first 100
  // records from the preview table, which would silently truncate large
  // lists). Accepts an optional `overridePurchase` so the post-payment
  // auto-send flow doesn't race React's state update cycle.
  const handleSendToDoorNoc = async (overridePurchase) => {
    if (!currentUser) {
      alert('Please log in to send data to DoorNoc.');
      return;
    }
    const purchase = overridePurchase || latestPurchase;
    if (!purchase) {
      alert('Please purchase this list first, then you can send it to DoorNoc.');
      return;
    }

    setIsSendingToDoorNoc(true);
    try {
      const result = await bridgeService.sendToDoorNoc({
        state: selectedState || activeFilters.State || 'ok',
        filters: activeFilters,
        purchaseId: purchase.id
      });

      if (result.success) {
        setTransferResult({
          voterCount: result.voterCount,
          expiresAt: result.expiresAt
        });
      } else {
        alert('Failed to send to DoorNoc. Please try again.');
      }
    } catch (error) {
      console.error('Error sending to DoorNoc:', error);
      // Try to pull the server error message out of the failed fetch — our
      // bridgeService throws with 'bridge send-to-doornoc failed (STATUS): BODY'.
      const msg = error?.message || '';
      if (msg.includes('413')) {
        alert('This list is too large to transfer to DoorNoc right now (max 50,000 voters). Narrow your search or contact support.');
      } else if (msg.toLowerCase().includes('doornoc profile')) {
        if (window.confirm('You need a free DoorNoc profile to receive voter lists.\n\nOpen app.doornoc.com to create one? (takes 30 seconds)')) {
          window.open('https://app.doornoc.com/onboarding', '_blank', 'noopener,noreferrer');
        }
      } else {
        alert('Failed to send to DoorNoc. Please try again.');
      }
    } finally {
      setIsSendingToDoorNoc(false);
    }
  };

  // Handle download - open checkout popup
  const handleDownload = () => {
    if (!currentUser) {
      alert('Please log in to download voter data.');
      return;
    }
    
    if (totalCount === 0) {
      alert('No voter data available to download.');
      return;
    }
    
    setIsCheckoutOpen(true);
  };

  // Handle successful payment — open the success modal IMMEDIATELY in a
  // "preparing" state so the user doesn't stare at a frozen UI for the
  // several seconds it takes to generate the download. When create-link
  // resolves we flip the modal to "ready" and surface the Download button.
  // If the user started from "Send to DoorNoc", we skip the download modal
  // and fire the bridge transfer after download link is ready.
  const handlePaymentSuccess = async (purchase) => {
    setLatestPurchase(purchase);
    const wasSendIntent = sendToDoorNocIntent;
    setSendToDoorNocIntent(false);

    setPurchaseSuccess({
      purchase,
      state: 'preparing',
      intent: wasSendIntent ? 'send-to-doornoc' : 'download'
    });

    try {
      const response = await authFetch('/api/mailvotes/downloads/create-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          searchParameters: activeFilters,
          purchaseId: purchase.id,
          format: 'mailing'
        })
      });

      const data = await response.json();

      if (!response.ok || data.success === false || data.error) {
        throw new Error(data.message || data.error || 'Failed to create download link');
      }

      if (wasSendIntent) {
        // User started from "Open in DoorNoc" — redirect straight into the
        // DoorNoc wizard with this new purchase pre-selected.
        setPurchaseSuccess(null);
        window.location.assign(
          `https://app.doornoc.com/routes/create?wizard=1&mailvotesPurchaseId=${purchase.id}`
        );
      } else {
        setPurchaseSuccess({
          purchase,
          state: 'ready',
          downloadUrl: data.downloadUrl,
          recordCount: data.recordCount,
          expiresAt: data.expiresAt
        });
      }
    } catch (error) {
      console.error('Error creating download after payment:', error);
      setPurchaseSuccess({
        purchase,
        state: 'error',
        error: error.message || 'Failed to create download link'
      });
    }
  };

  // Sort data
  const sortedData = [...voterData].sort((a, b) => {
    const aValue = a[sortField] || '';
    const bValue = b[sortField] || '';

    // Ages (and other numbers) sort numerically, not as text ("9" > "45").
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }

    if (sortDirection === 'asc') {
      return aValue.toString().localeCompare(bValue.toString());
    } else {
      return bValue.toString().localeCompare(aValue.toString());
    }
  });

  // Paginate data
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = sortedData.slice(startIndex, startIndex + pageSize);

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Custom styles for the component
  const styles = {
    inputGroup: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      alignItems: 'strecth',
      width: '100%'
    },
    standardInput: {
      flex: '1 0 160px',
      maxWidth: '220px'
    },
    narrowInput: {
      flex: '1 0 120px',
      maxWidth: '180px'
    },
    wideInput: {
      flex: '1 0 200px',
      maxWidth: '250px'
    },
    buttonContainer: {
      display: 'flex',
      gap: '10px',
      alignItems: 'flex-start',
    },
    button: {
      padding: '8px 16px',
      backgroundColor: '#112030',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      height: '34px',
      marginTop: '0',
      whiteSpace: 'nowrap'
    }
  };

  return (
    <div id="voterContent" className="content-section">
      {/* Search Pane */}
      <div id="searchPane" className="search-pane">
        <div className="input-group" style={styles.inputGroup}>
          <input type="text" id="firstNameInput" placeholder="First Name" style={styles.standardInput} />
          <input type="text" id="lastNameInput" placeholder="Last Name" style={styles.standardInput} />
          <input type="text" id="cityInput" placeholder="City" style={styles.standardInput} />
          <select id="stateInput" style={styles.standardInput} onChange={(e) => handleStateChange(e.target.value)}>
            <option value="">Select State</option>
            {(availableStates.length > 0 ? availableStates : ['AR', 'FL', 'NC', 'OH', 'OK', 'PA']).map((state, index) => (
              <option key={index} value={state}>{state}</option>
            ))}
          </select>
          <select id="countyInput" style={styles.wideInput}>
            <option value="">Select County</option>
            {counties.length > 0 ? (
              counties.map((county, index) => (
                <option key={index} value={county}>{county}</option>
              ))
            ) : null}
          </select>
          <select id="politicalAffiliationInput" style={styles.wideInput}>
            <option value="">Select Political Affiliation</option>
            <option value="DEM">Democrat</option>
            <option value="REP">Republican</option>
            <option value="IND">Independent</option>
          </select>
          <input type="text" id="congressionalDistInput" placeholder="Cong. District" style={styles.narrowInput} />
          <input type="text" id="stateSenateDistInput" placeholder="State Senate Dist." style={styles.narrowInput} />
          <input type="text" id="stateHouseDistInput" placeholder="State House Dist." style={styles.narrowInput} />
          <input type="text" id="schoolDistInput" placeholder="School District" style={styles.narrowInput} />
          <input type="text" id="muniSubInput" placeholder="Muni Sub" style={styles.narrowInput} />
          <input type="text" id="precinctInput" placeholder="Precinct" style={styles.narrowInput} />
          <input type="text" id="streetNameInput" placeholder="Street Name" style={styles.standardInput} />
          <input type="text" id="streetNumInput" placeholder="Street Number" style={styles.narrowInput} />
          <input type="text" id="voterIdInput" placeholder="Voter ID" style={styles.narrowInput} />
          <select id="statusInput" style={styles.narrowInput}>
            <option value="">Select Status</option>
            <option value="A">Active</option>
            <option value="I">Inactive</option>
          </select>

          {/* Buttons aligned with inputs - responsive */}
          <div className="button-container-responsive" style={styles.buttonContainer}>
            <button
              id="walkthroughButton"
              style={{
                ...styles.button,
                backgroundColor: '#28a745',
                fontWeight: 'bold',
                padding: '10px 20px',
                fontSize: '15px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
              onClick={openWalkthroughModal}
            >
              Start Query Wizard
            </button>
            <button
              id="searchButton"
              style={styles.button}
              onClick={handleSearch}
            >
              Search
            </button>
            {voterData.length > 0 && (
              <>
                <button
                  id="downloadButton"
                  style={styles.button}
                  onClick={handleDownload}
                  title="Buy the mailing-ready CSV"
                >
                  Purchase & Download CSV
                </button>
                <button
                  style={{ ...styles.button, backgroundColor: '#17688e' }}
                  onClick={() => {
                    if (latestPurchase) {
                      // Already paid — redirect straight into DoorNoc with the
                      // purchase pre-selected in the wizard.
                      window.location.assign(
                        `https://app.doornoc.com/routes/create?wizard=1&mailvotesPurchaseId=${latestPurchase.id}`
                      );
                    } else {
                      // No purchase yet — run checkout first, then redirect.
                      setSendToDoorNocIntent(true);
                      setIsCheckoutOpen(true);
                    }
                  }}
                  disabled={isSendingToDoorNoc}
                  title={latestPurchase
                    ? 'Open DoorNoc with this list pre-selected for canvassing'
                    : 'Purchase is required first. We\'ll walk you through it.'}
                >
                  {isSendingToDoorNoc ? 'Opening…' : 'Open in DoorNoc'}
                </button>
                <button
                  style={{ ...styles.button, backgroundColor: '#4CAF50' }}
                  onClick={() => {
                    saveMailingListSearch(activeFilters, totalCount);
                    navigate('/postcards');
                  }}
                  title="Design a postcard mailer and send it to the voters in this search"
                >
                  Mail postcards
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div id="recordCountDisplay">
        Total Records Found: {totalCount.toLocaleString()} {recordCount < totalCount && `(showing ${recordCount})`}
        {priceQuote && (
          <span style={{ marginLeft: 12, color: '#17688e', fontWeight: 600 }}>
            · Price: ${formatPrice(priceQuote.priceCents)} ({priceQuote.tierLabel})
          </span>
        )}
        {quoteError && !priceQuote && totalCount > 0 && (
          <span style={{ marginLeft: 12, color: '#a51817' }}>· {quoteError}</span>
        )}
        {latestPurchase && (
          <span style={{ marginLeft: 12, color: '#2e7d32', fontSize: '0.9em', fontWeight: 600 }}>
            · ✓ Purchased — you can now send to DoorNoc
          </span>
        )}
      </div>

      {/* Page Size Selector */}
      {voterData.length > 0 && (
        <div style={{ margin: '10px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span>Show:</span>
          <select 
            value={pageSize} 
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            style={{ padding: '4px 8px' }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span>entries</span>
        </div>
      )}

      {/* Voter Table */}
      <div id="voterTable" className="voter-table">
        <table id="voterDataTable" className="display" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th 
                onClick={() => handleSort('LastName')}
                style={{ 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                Last Name {sortField === 'LastName' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                onClick={() => handleSort('FirstName')}
                style={{ 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                First Name {sortField === 'FirstName' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                onClick={() => handleSort('Age')}
                style={{ 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                Age {sortField === 'Age' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                onClick={() => handleSort('PolitalAff')}
                style={{ 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                Political Affiliation {sortField === 'PolitalAff' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                onClick={() => handleSort('MuniSub')}
                style={{ 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                Muni Sub {sortField === 'MuniSub' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                onClick={() => handleSort('StreetNum')}
                style={{ 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                Street Number {sortField === 'StreetNum' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                onClick={() => handleSort('StreetName')}
                style={{ 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                Street Name {sortField === 'StreetName' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                onClick={() => handleSort('City')}
                style={{ 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                City {sortField === 'City' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th
                onClick={() => handleSort('County')}
                style={{
                  padding: '10px',
                  border: '1px solid #ddd',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                County {sortField === 'County' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th
                onClick={() => handleSort('UnitNum')}
                style={{
                  padding: '10px',
                  border: '1px solid #ddd',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                Unit {sortField === 'UnitNum' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th
                onClick={() => handleSort('Zip')}
                style={{
                  padding: '10px',
                  border: '1px solid #ddd',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                Zip {sortField === 'Zip' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th
                style={{
                  padding: '10px',
                  border: '1px solid #ddd',
                  minWidth: '200px'
                }}
              >
                Full Address
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="12" className="spinner-cell" style={{ padding: '20px', textAlign: 'center' }}>
                  <div className="spinner-inline"></div>
                </td>
              </tr>
            ) : (
              paginatedData.map((voter, index) => {
                // Build full address
                const fullAddress = [
                  voter.StreetNum,
                  voter.StreetName,
                  voter.UnitNum ? `#${voter.UnitNum}` : '',
                  voter.City,
                  voter.State,
                  voter.Zip
                ].filter(Boolean).join(' ').trim() || 'N/A';

                return (
                  <tr key={`voter-${index}-${voter.VoterID || index}`} style={{ backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white' }}>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{voter.LastName || 'N/A'}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{voter.FirstName || 'N/A'}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{voter.Age || calculateAge(voter.DateOfBirth) || 'N/A'}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{voter.PolitalAff || 'N/A'}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{voter.MuniSub || 'N/A'}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{voter.StreetNum || 'N/A'}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{voter.StreetName || 'N/A'}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{voter.City || 'N/A'}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{voter.County || voter.County_Desc || 'N/A'}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{voter.UnitNum || '-'}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{voter.Zip || 'N/A'}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', minWidth: '200px' }}>{fullAddress}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {voterData.length > 0 && totalPages > 1 && (
        <div style={{ 
          margin: '20px 0', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '10px' 
        }}>
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: '8px 12px',
              backgroundColor: currentPage === 1 ? '#ccc' : '#112030',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            Previous
          </button>
          
          <span>
            Page {currentPage} of {totalPages} ({voterData.length} total records)
          </span>
          
          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 12px',
              backgroundColor: currentPage === totalPages ? '#ccc' : '#112030',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
            }}
          >
            Next
          </button>
        </div>
      )}

      {/* Query Walkthrough Modal with Elections */}
      <QueryWalkthroughModal
        isOpen={isWalkthroughModalOpen}
        onClose={closeWalkthroughModal}
        onApplyFilters={handleApplyWalkthroughFilters}
        elections={elections}
        isElectionsLoading={isElectionsLoading}
        formatDate={formatDate}
      />

      {/* Checkout Popup for Payment */}
      <CheckoutPopup
        isOpen={isCheckoutOpen}
        onClose={() => {
          setIsCheckoutOpen(false);
          setSendToDoorNocIntent(false);
        }}
        amount={priceQuote ? priceQuote.priceCents : 0}
        productType={priceQuote ? priceQuote.productType : null}
        tierLabel={priceQuote ? priceQuote.tierLabel : ''}
        quoteError={quoteError}
        searchParameters={activeFilters}
        onSuccess={handlePaymentSuccess}
        isCountLoading={isCountLoading}
        totalCount={totalCount}
        intent={sendToDoorNocIntent ? 'send-to-doornoc' : 'download'}
      />

      {/* Transfer success modal — replaces the old alert() */}
      {transferResult && (
        <div
          onClick={() => setTransferResult(null)}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2000, padding: 20
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white', borderRadius: 16, maxWidth: 480,
              width: '100%', padding: 32, boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
          >
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              backgroundColor: '#e8f5e9', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              marginBottom: 20
            }}>
              <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#2e7d32" strokeWidth="3">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 style={{ margin: '0 0 8px', color: '#112030', fontSize: 22 }}>
              {transferResult.voterCount.toLocaleString()} voters sent to DoorNoc!
            </h2>
            <p style={{ color: '#555', fontSize: 15, lineHeight: 1.5, margin: '0 0 20px' }}>
              Open DoorNoc, create a new campaign or route, and pick <strong>Import from MailVotes</strong> to use this list. The transfer expires in 24 hours.
            </p>
            <p style={{ color: '#b26500', fontSize: 13, lineHeight: 1.5, margin: '0 0 24px', backgroundColor: '#fff3e0', padding: 12, borderRadius: 8, border: '1px solid #ffe0b2' }}>
              Note: You need a DoorNoc subscription (or the free DoorNoc account with a plan) to import lists there.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setTransferResult(null)}
                style={{
                  padding: '10px 20px', backgroundColor: '#f5f5f5',
                  color: '#333', border: '1px solid #ccc', borderRadius: 8,
                  fontSize: 14, fontWeight: 500, cursor: 'pointer'
                }}
              >
                Close
              </button>
              <button
                onClick={() => {
                  window.open('https://app.doornoc.com/dashboard', '_blank', 'noopener,noreferrer');
                  setTransferResult(null);
                }}
                style={{
                  padding: '10px 20px', backgroundColor: '#17688e',
                  color: 'white', border: 'none', borderRadius: 8,
                  fontSize: 14, fontWeight: 600, cursor: 'pointer'
                }}
              >
                Open DoorNoc →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post-purchase success modal — opens immediately after Stripe
          confirms with a "preparing" spinner, flips to "ready" when the
          download link is available, or to "error" if CSV generation
          fails. The user never faces a frozen/blank UI after paying. */}
      {purchaseSuccess && (
        <div
          onClick={() => {
            if (purchaseSuccess.state === 'preparing') return;
            setPurchaseSuccess(null);
          }}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 2000, padding: 20
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white', borderRadius: 16, maxWidth: 520,
              width: '100%', padding: 32, boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
          >
            {purchaseSuccess.state === 'preparing' && (
              <>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  backgroundColor: '#e8f5e9', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  marginBottom: 20
                }}>
                  <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#2e7d32" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h2 style={{ margin: '0 0 8px', color: '#112030', fontSize: 22 }}>
                  Payment successful!
                </h2>
                <p style={{ color: '#555', fontSize: 15, lineHeight: 1.5, margin: '0 0 16px' }}>
                  {purchaseSuccess.intent === 'send-to-doornoc'
                    ? 'Preparing your voter list for transfer to DoorNoc…'
                    : 'Preparing your download — this may take a few seconds for large lists…'}
                </p>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '14px 18px', backgroundColor: '#f5f5f5',
                  borderRadius: 8, marginTop: 8
                }}>
                  <div style={{
                    width: 20, height: 20, border: '2.5px solid #ccc',
                    borderTopColor: '#17688e', borderRadius: '50%',
                    animation: 'mvSpin 1s linear infinite'
                  }} />
                  <span style={{ fontSize: 14, color: '#555' }}>
                    Building CSV from your purchase…
                  </span>
                </div>
                <style>{`@keyframes mvSpin { to { transform: rotate(360deg); } }`}</style>
              </>
            )}

            {purchaseSuccess.state === 'ready' && (
              <>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  backgroundColor: '#e8f5e9', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  marginBottom: 20
                }}>
                  <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#2e7d32" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h2 style={{ margin: '0 0 8px', color: '#112030', fontSize: 22 }}>
                  Your download is ready
                </h2>
                <p style={{ color: '#555', fontSize: 15, lineHeight: 1.5, margin: '0 0 8px' }}>
                  <strong>{purchaseSuccess.recordCount?.toLocaleString()}</strong> voter records in your CSV.
                </p>
                <p style={{ color: '#888', fontSize: 13, margin: '0 0 20px' }}>
                  This link expires in 5 minutes. You can always re-download from <strong>My Profile → Purchase History</strong> (up to 4 times per 24h).
                </p>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setPurchaseSuccess(null)}
                    style={{
                      padding: '10px 20px', backgroundColor: '#f5f5f5',
                      color: '#333', border: '1px solid #ccc', borderRadius: 8,
                      fontSize: 14, fontWeight: 500, cursor: 'pointer'
                    }}
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      const purchase = purchaseSuccess.purchase;
                      setPurchaseSuccess(null);
                      window.location.assign(
                        `https://app.doornoc.com/routes/create?wizard=1&mailvotesPurchaseId=${purchase.id}`
                      );
                    }}
                    style={{
                      padding: '10px 20px', backgroundColor: '#17688e',
                      color: 'white', border: 'none', borderRadius: 8,
                      fontSize: 14, fontWeight: 600, cursor: 'pointer'
                    }}
                  >
                    Open in DoorNoc
                  </button>
                  <button
                    onClick={() => {
                      window.open(purchaseSuccess.downloadUrl, '_blank', 'noopener,noreferrer');
                    }}
                    style={{
                      padding: '10px 20px', backgroundColor: '#2e7d32',
                      color: 'white', border: 'none', borderRadius: 8,
                      fontSize: 14, fontWeight: 600, cursor: 'pointer'
                    }}
                  >
                    Download CSV
                  </button>
                </div>
              </>
            )}

            {purchaseSuccess.state === 'error' && (
              <>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  backgroundColor: '#fff3e0', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  marginBottom: 20
                }}>
                  <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#e65100" strokeWidth="3">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <h2 style={{ margin: '0 0 8px', color: '#112030', fontSize: 22 }}>
                  Payment succeeded, download failed
                </h2>
                <p style={{ color: '#555', fontSize: 15, lineHeight: 1.5, margin: '0 0 20px' }}>
                  Your payment went through, but we couldn't build the CSV: <strong>{purchaseSuccess.error}</strong>
                </p>
                <p style={{ color: '#666', fontSize: 14, margin: '0 0 24px' }}>
                  You can retry from <strong>My Profile → Purchase History</strong> at any time.
                </p>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setPurchaseSuccess(null)}
                    style={{
                      padding: '10px 20px', backgroundColor: '#17688e',
                      color: 'white', border: 'none', borderRadius: 8,
                      fontSize: 14, fontWeight: 600, cursor: 'pointer'
                    }}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

export default VotersPage;